import { memo, useMemo } from "react";

import { Column, TagTextAlert } from "@bridgesplit/ui";
import { getZcCollateral, useUserAvailableAssets, useWhirlpoolUpdatePositionTransaction } from "@bridgesplit/abf-react";
import { bsMath, decimalsToBps, LOADING_ERROR, Result } from "@bridgesplit/utils";
import { GetUpdatePositionTxnArgs } from "@bridgesplit/abf-sdk";

import { AppButton } from "../../../../common";
import { useWhirlpoolOpenPositionBalances, useOpenPositionLtv } from "./util";
import { useWhirlpoolContext } from "../WhirlpoolContext";
import { useTransactionSender } from "../../../../transactions";
import { getMaxBalanceBalanceWarning } from "../../util";

export default memo(function OpenPositionCta() {
    const {
        openPositionForm,
        poolData,
        openPositionQuote,
        whirlpoolPosition,
        slippageController: { isAutoSlippage }
    } = useWhirlpoolContext();
    const { insufficientBalanceMessage } = useWhirlpoolOpenPositionBalances();
    const updateWhirlpool = useUpdateWhirlpool();
    const { insufficientCollateral } = useOpenPositionLtv();
    const { data: userAssets } = useUserAvailableAssets({});

    const rawUserTokenAAmount = useMemo(() => {
        return userAssets?.find((asset) => asset.metadata.mint === whirlpoolPosition.tokenA.metadata.mint)?.amount ?? 0;
    }, [userAssets, whirlpoolPosition.tokenA.metadata.mint]);

    const rawUserTokenBAmount = useMemo(() => {
        return userAssets?.find((asset) => asset.metadata.mint === whirlpoolPosition.tokenB.metadata.mint)?.amount ?? 0;
    }, [userAssets, whirlpoolPosition.tokenB.metadata.mint]);

    const alertError = useMemo(() => {
        // don't show alert with no user input
        if (openPositionForm.status === undefined || !poolData) return undefined;
        if (insufficientBalanceMessage) return insufficientBalanceMessage;
        if (insufficientCollateral) return "Insufficient collateral for your loan debt";

        return undefined;
    }, [openPositionForm.status, poolData, insufficientBalanceMessage, insufficientCollateral]);

    const ctaError = useMemo(() => {
        if (!openPositionForm.tokenAAmount && !openPositionForm.tokenBAmount) return "Enter an amount";
        if (openPositionForm.tickLower === undefined || openPositionForm.tickUpper === undefined)
            return "Enter liquidity range";
        if (openPositionForm.status === undefined) return "No changes made";
        if (openPositionForm.status === "error") return "No quote found";
        if (!poolData || !openPositionQuote) return "Loading";
        return undefined;
    }, [
        openPositionForm.tokenAAmount,
        openPositionForm.tokenBAmount,
        openPositionForm.tickLower,
        openPositionForm.tickUpper,
        openPositionForm.status,
        poolData,
        openPositionQuote
    ]);

    const ctaWarning = useMemo(() => {
        return getMaxBalanceBalanceWarning(
            isAutoSlippage,
            whirlpoolPosition.tokenA.metadata,
            whirlpoolPosition.tokenB.metadata,
            bsMath.sub(openPositionForm.maxTokenAAmount, whirlpoolPosition.tokenA.amount),
            bsMath.sub(openPositionForm.maxTokenBAmount, whirlpoolPosition.tokenB.amount),
            rawUserTokenAAmount,
            rawUserTokenBAmount
        );
    }, [
        openPositionForm.maxTokenAAmount,
        openPositionForm.maxTokenBAmount,
        whirlpoolPosition.tokenA.metadata,
        whirlpoolPosition.tokenB.metadata,
        whirlpoolPosition.tokenA.amount,
        whirlpoolPosition.tokenB.amount,
        isAutoSlippage,
        rawUserTokenAAmount,
        rawUserTokenBAmount
    ]);

    return (
        <Column spacing={1}>
            {!!alertError && (
                <TagTextAlert color="error" icon="warning">
                    {alertError}
                </TagTextAlert>
            )}
            {!!ctaWarning && !alertError && (
                <TagTextAlert color="warning" icon="warning">
                    {ctaWarning}
                </TagTextAlert>
            )}
            <AppButton
                asyncCta={{ onClickWithResult: updateWhirlpool }}
                isTransaction
                color="secondary"
                disabled={!!ctaError || !!alertError}
            >
                {ctaError ?? "Modify position"}
            </AppButton>
        </Column>
    );
});

function useUpdateWhirlpool() {
    const {
        openPositionQuote,
        loanExpanded,
        reset,
        slippageController: { isAutoSlippage }
    } = useWhirlpoolContext();
    const send = useTransactionSender();
    const updatePosition = useWhirlpoolUpdatePositionTransaction();
    return async () => {
        if (!openPositionQuote) return Result.errFromMessage(LOADING_ERROR);

        const positionMint = getZcCollateral(loanExpanded)?.assetMint;
        if (!positionMint) return Result.errFromMessage("No position mint found");

        const params: GetUpdatePositionTxnArgs = {
            loan: loanExpanded.loan.address,
            positionMint,
            transferParams: {
                collateralIndex: 0,
                liquidityAmount: openPositionQuote.liquidity.toNumber(),
                transferParams: isAutoSlippage
                    ? { Liquidity: { slippageToleranceBps: decimalsToBps(0.1, "bps") } }
                    : {
                          TokenAmounts: {
                              tokenAAmount: openPositionQuote.maxTokenA.toNumber(),
                              tokenBAmount: openPositionQuote.maxTokenB.toNumber()
                          }
                      },
                tickLowerIndex: openPositionQuote.tickLowerIndex,
                tickUpperIndex: openPositionQuote.tickUpperIndex
            }
        };

        return await send(updatePosition, params, { sendOptions: { onSuccess: reset }, preventAutoDialogClose: true });
    };
}
